import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { faCopy, faDownload, faPaperPlane, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { environment } from 'src/environments/environment';
import { CurrentService } from 'src/app/services/current.service';
import { ToastService } from 'src/app/services/toast.service';
import { ComPlusAdminService } from 'src/app/services/complus-admin/complus-admin.service';
import { COMPLUS_ADMIN_SECRET_SEQUENCE } from 'src/app/services/complus-admin/complus-admin-secret-command';
import {
  formatComPlusCustomerId,
  ComPlusAdminAccess,
  ComPlusAdminQuote,
  ComPlusSendResult,
} from 'src/app/services/complus-admin/complus-admin';

type LineForm = FormGroup<{
  ean: FormControl<string>;
  description: FormControl<string>;
  note: FormControl<string>;
  quantity: FormControl<number>;
  unitPrice: FormControl<number>;
}>;

/**
 * Écran d'administration COM+ : saisie libre d'un devis (EAN, client, vendeur, magasin),
 * génération du XML COM+ par l'API puis envoi au point d'accès COM+ du magasin.
 * Disponible sur la version vendeur (administrateurs) et, si le réglage est ouvert, sur le site client.
 */
@Component({
  selector: 'ng-casto-complus-admin',
  templateUrl: './complus-admin.component.html',
  styleUrl: './complus-admin.component.scss',
})
export class ComPlusAdminComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly comPlusAdminService = inject(ComPlusAdminService);
  private readonly current = inject(CurrentService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  protected readonly faPlus = faPlus;
  protected readonly faTrash = faTrash;
  protected readonly faCopy = faCopy;
  protected readonly faDownload = faDownload;
  protected readonly faPaperPlane = faPaperPlane;
  /** Commande secrète rappelée dans les bandeaux, issue de la même constante que l'écouteur clavier. */
  protected readonly secretCommand = COMPLUS_ADMIN_SECRET_SEQUENCE;

  protected access: ComPlusAdminAccess | null = null;
  protected loading = true;
  protected togglingCustomerAccess = false;
  /** Horloge rafraîchie périodiquement pour refléter l'expiration de l'accès client sans recharger. */
  protected now = Date.now();
  private readonly clock = setInterval(() => (this.now = Date.now()), 15000);
  protected generating = false;
  protected sending = false;
  protected generationError = '';
  protected sendError = '';
  protected xml = '';
  protected xmlFilename = 'complus.xml';
  protected sendResult: ComPlusSendResult | null = null;

  protected readonly form = this.formBuilder.nonNullable.group({
    componentId: ['HOMEBYME', [Validators.required, Validators.maxLength(50)]],
    type: ['P', [Validators.required, Validators.maxLength(10)]],
    projectReferencePrefix: ['HBM', [Validators.maxLength(20)]],
    projectName: ['', [Validators.maxLength(100)]],
    customerId: ['', [Validators.required, Validators.maxLength(50)]],
    vendorId: ['', [Validators.maxLength(50)]],
    storeId: ['', [Validators.required, Validators.maxLength(20)]],
    contactedByPhone: ['none' as 'none' | 'true' | 'false'],
    lines: this.formBuilder.array<LineForm>([]),
  });

  constructor() {
    // Le préfixe SQ_ dépend de l'émetteur : on le réapplique quand celui-ci change.
    this.form.controls.componentId.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.normalizeCustomerId());
  }

  get lines(): FormArray<LineForm> {
    return this.form.controls.lines;
  }

  get total(): number {
    return this.lines.controls.reduce((sum, line) => {
      const quantity = Number(line.controls.quantity.value) || 0;
      const unitPrice = Number(line.controls.unitPrice.value) || 0;
      return sum + quantity * unitPrice;
    }, 0);
  }

  /** Accès client ouvert et non expiré. */
  get customerAccessActive(): boolean {
    const expiresAt = this.access?.customerAccessExpiresAt;
    return !!this.access?.customerAccessEnabled && !!expiresAt && new Date(expiresAt).getTime() > this.now;
  }

  async ngOnInit(): Promise<void> {
    this.addLine();
    this.prefillFromVendor();
    this.access = await this.comPlusAdminService.getAccess();
    this.loading = false;
  }

  ngOnDestroy(): void {
    clearInterval(this.clock);
  }

  /** Applique la règle COM+ du préfixe SQ_ à l'identifiant client saisi. */
  protected normalizeCustomerId(): void {
    const { customerId, componentId } = this.form.controls;
    const formatted = formatComPlusCustomerId(customerId.value, componentId.value);
    if (formatted !== customerId.value) {
      customerId.setValue(formatted);
    }
  }

  protected addLine(): void {
    const line: LineForm = this.formBuilder.nonNullable.group({
      ean: ['', [Validators.required, Validators.maxLength(20)]],
      description: ['', [Validators.maxLength(100)]],
      note: ['', [Validators.maxLength(500)]],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
    });
    this.lines.push(line);
  }

  protected duplicateLine(index: number): void {
    this.addLine();
    this.lines.at(this.lines.length - 1).setValue(this.lines.at(index).getRawValue());
  }

  protected removeLine(index: number): void {
    this.lines.removeAt(index);
    if (this.lines.length === 0) {
      this.addLine();
    }
  }

  protected async toggleCustomerAccess(enabled: boolean): Promise<void> {
    if (
      enabled &&
      !window.confirm(
        this.translate.instant('complus-admin.customer-access.enable_confirm', { command: this.secretCommand })
      )
    ) {
      return;
    }

    this.togglingCustomerAccess = true;
    try {
      this.access = await this.comPlusAdminService.setCustomerAccess(enabled);
      this.now = Date.now();
    } finally {
      this.togglingCustomerAccess = false;
    }
  }

  protected async generate(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.generating || this.form.invalid) {
      return;
    }

    this.generating = true;
    this.generationError = '';
    this.sendResult = null;
    this.sendError = '';
    try {
      const result = await this.comPlusAdminService.generateXml(this.toQuote());
      this.xml = result.xml;
      this.xmlFilename = result.filename;
    } catch (err: any) {
      this.generationError = err?.error?.message ?? err?.message ?? this.translate.instant('generic.error');
    } finally {
      this.generating = false;
    }
  }

  protected async send(): Promise<void> {
    if (this.sending || !this.xml) {
      return;
    }

    // Point d'accès unique par environnement : le magasin n'est pas vérifié, pour pouvoir tester des magasins inconnus.
    const storeId =
      /<PreferredStoreID>\s*([^<]*?)\s*<\/PreferredStoreID>/.exec(this.xml)?.[1] || this.form.controls.storeId.value;
    const endpoint = environment.dotComPlusURL;
    const confirmationKey = /prod/i.test(endpoint) ? 'complus-admin.send.confirm_prod' : 'complus-admin.send.confirm';
    if (!window.confirm(this.translate.instant(confirmationKey, { store: storeId, endpoint }))) {
      return;
    }

    this.sending = true;
    this.sendError = '';
    this.sendResult = null;
    try {
      this.sendResult = await this.comPlusAdminService.send(endpoint, this.xml);
    } catch (err: any) {
      const status = err?.status ? `HTTP ${err.status} ` : '';
      const body = typeof err?.error === 'string' ? err.error : (err?.message ?? '');
      this.sendError = `${status}${body}`.trim() || this.translate.instant('generic.error');
    } finally {
      this.sending = false;
    }
  }

  protected async copyXml(): Promise<void> {
    await navigator.clipboard.writeText(this.xml);
    this.toastService.showSuccessToast(
      this.translate.instant('generic.success'),
      this.translate.instant('complus-admin.xml.copied')
    );
  }

  protected downloadXml(): void {
    const url = URL.createObjectURL(new Blob([this.xml], { type: 'application/xml' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = this.xmlFilename;
    link.click();
    URL.revokeObjectURL(url);
  }

  protected isInvalid(control: { invalid: boolean; touched: boolean }): boolean {
    return control.invalid && control.touched;
  }

  private prefillFromVendor(): void {
    const vendor = this.current.vendor;
    if (!vendor) {
      return;
    }

    this.form.patchValue({
      vendorId: vendor.sapId ?? '',
      storeId: vendor.store?.SAPStoreId ?? '',
    });
  }

  private toQuote(): ComPlusAdminQuote {
    const value = this.form.getRawValue();
    return {
      componentId: value.componentId,
      type: value.type,
      projectReferencePrefix: value.projectReferencePrefix,
      projectName: value.projectName,
      customerId: formatComPlusCustomerId(value.customerId, value.componentId),
      vendorId: value.vendorId,
      storeId: value.storeId,
      contactedByPhone: value.contactedByPhone === 'none' ? null : value.contactedByPhone === 'true',
      lines: value.lines.map(line => ({ ...line, quantity: Number(line.quantity), unitPrice: Number(line.unitPrice) })),
    };
  }
}
